<script setup lang="ts">
/**
 * The one actionable card of the Today page (IPD-2976 10.2).
 *
 * Three places render it and must never disagree: the Upcoming tile shows task #1 as a boxed
 * card, the task-list tile shows the same list as flush rows (IPD-2977), and the Upcoming
 * tile's recommendation face reuses the very same card for a recommended programme — the
 * Figma sheet draws them identically. Hence one component with two shapes rather than three
 * lookalikes drifting apart.
 *
 * Presentational only: it neither picks the item nor knows why it is first. An item that opens
 * a modal (today, only a questionnaire) arrives with `to: null` and reports activation through
 * `select`; everything else is a link, so middle-click and "open in new tab" keep working.
 */
import {computed} from 'vue';
import {useI18n} from 'vue-i18n';
import LazyImage from '@/vue/atoms/lazy-image.vue';
import type {TodayCardItem} from '@/ts/types/component/today-page';
import {todayCardKindIcon, todayCardKindLabelKey} from '@/ts/composables/pure/today-label-keys';
import {progressFractionToPct} from '@/ts/utils/pure-functions';

const props = withDefaults(defineProps<{
  item: TodayCardItem;
  /** `featured` is the boxed Upcoming card, `row` the flush list line. */
  variant?: 'featured' | 'row';
}>(), {
  variant: 'featured',
});

const emit = defineEmits<{
  select: [item: TodayCardItem];
}>();

const {t: $t} = useI18n();

// One source for the thumbnail size: the pixel value reaches the stylesheet through v-bind,
// so the image request and the box it fills can never drift apart.
const THUMB_SIZE_PX = 42;
const thumbSize = `${THUMB_SIZE_PX}px`;

/**
 * The programme or challenge the item belongs to, falling back to its kind when it stands
 * alone. The prototype mixes both criteria in the same list on purpose: the parent name is
 * what tells two sessions of different programmes apart, and a questionnaire has none.
 *
 * An empty string counts as "none": the CMS lets a title be blank, and `??` would hand the
 * card a blank eyebrow rather than the kind label the design falls back to.
 */
const eyebrow = computed<string>(() =>
  props.item.parentName || $t(todayCardKindLabelKey[props.item.kind]));

const kindIcon = computed<string>(() => todayCardKindIcon[props.item.kind]);

// The bar is part of the featured card only — the list trades it for density, as in the design.
const progressPct = computed<number | null>(() =>
  props.variant === 'featured' && props.item.progress !== null
    ? progressFractionToPct(props.item.progress)
    : null);

// Progress, the kind and the done marker are visual-only, so they have to live in the
// accessible name: an aria-label suppresses every descendant, sr-only spans included.
const ariaLabel = computed<string>(() => {
  const kindLabel = $t(todayCardKindLabelKey[props.item.kind]);
  const parts = [eyebrow.value, props.item.title];
  // The eyebrow already names the kind for an item with no parent; when it names the programme
  // instead, the kind is appended so the accessible name still carries it — the list rows have
  // nothing else that conveys it, and even the featured card's thumbnail glyph is invisible to
  // the accessible name. Same falsy test as the eyebrow, so a blank parent name does not
  // announce the kind twice. Applies to both variants on purpose.
  //
  // Appended after the title rather than inserted between the two visible lines: the name has to
  // contain the visible text in order (WCAG 2.5.3), or voice control cannot activate the card by
  // reading it aloud — and axe flags the mismatch as a serious violation the page's own a11y
  // test throws on.
  if (eyebrow.value !== kindLabel) {
    parts.push(kindLabel);
  }
  if (progressPct.value !== null) {
    parts.push($t('complete_n', {n: progressPct.value}));
  }
  if (props.item.done) {
    parts.push($t('today_task_done'));
  }
  return parts.join(', ');
});

// Fires on every activation, navigation included, so a tile can react without having to
// distinguish the two shapes (analytics in IPD-2974 needs exactly that).
function onActivate(): void {
  emit('select', props.item);
}
</script>

<template>
  <component
    :is="item.to ? 'router-link' : 'button'"
    :to="item.to ?? undefined"
    :type="item.to ? undefined : 'button'"
    class="item-card"
    :class="[`item-card--${variant}`, {'item-card--done': item.done}]"
    :aria-label="ariaLabel"
    @click="onActivate"
  >
    <!-- The list rows carry no thumbnail: the design gives them the eyebrow and the title only,
         so five of them fit the tile and read as a list rather than as five stacked cards. -->
    <span v-if="variant === 'featured'" class="item-card__thumb" :class="`item-card__thumb--${item.kind}`">
      <lazy-image
        v-if="item.imageUrl"
        :src="item.imageUrl"
        :width="THUMB_SIZE_PX"
        :height="THUMB_SIZE_PX"
        skip-loader
        class="item-card__image"
      />
      <q-icon v-else :name="kindIcon" size="22px"/>
    </span>

    <span class="item-card__body">
      <span class="item-card__eyebrow">{{ eyebrow }}</span>
      <span class="item-card__title">{{ item.title }}</span>
      <span v-if="progressPct !== null" class="item-card__track">
        <span class="item-card__fill" :style="{width: `${progressPct}%`}"/>
      </span>
    </span>

    <q-icon v-if="variant === 'featured'" name="arrow_forward" size="24px" class="item-card__arrow"/>
    <!-- No `appear`, on purpose: the tick is meant to mark the moment a task is completed while
         the page is open, so it must not replay for tasks that were already done on load. -->
    <transition v-else name="item-card-mark" mode="out-in">
      <q-icon
        v-if="item.done"
        key="check"
        name="check"
        size="16px"
        class="item-card__check"
      />
      <q-icon
        v-else
        key="chevron"
        name="chevron_right"
        size="16px"
        class="item-card__chevron"
      />
    </transition>
  </component>
</template>

<style scoped lang="scss">
.item-card {
  @include focus-visible;

  display: flex;
  align-items: center;
  gap: var(--Spacing-spacing-3);
  width: 100%;
  border: none;
  text-align: left;
  text-decoration: none;
  color: var(--Neutral-UI-neutral-ui-100);
  cursor: pointer;
}

// Boxed card inside the Upcoming tile.
//
// Its own padding and its own gap, both a step wider than the base the row variant keeps: measured
// on the prototype, this card is padded 24 and separates its picture from its text by 16, where
// the shared values resolve to 16 and 12 on desktop. Scoped to this variant rather than raised on
// `.item-card`, since the list rows are drawn to a rhythm of their own.
.item-card--featured {
  gap: var(--Spacing-spacing-4);
  padding: var(--Spacing-spacing-5);
  border-radius: var(--today-radius-card);
  background: var(--today-surface-inset);
  transition: background 0.2s ease;

  &:hover,
  &:focus-visible {
    background: color-mix(in srgb, var(--today-surface-inset) 91%, var(--Neutral-UI-neutral-ui-100));
  }
}

// Flush line inside the task list: the rows sit directly on the tile, separated by rhythm only.
.item-card--row {
  padding: var(--Spacing-spacing-3) 0;
  background: none;
}

// Completion is reported while the page is open, so the copy fades into its done state rather
// than blinking (IPD-2977 11.2, design IPD-2906). The thumb transitions its opacity; the words
// transition the colour they switch to instead, since their dimming is a colour change now.
.item-card__thumb {
  transition: opacity 0.3s ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
}

.item-card__title,
.item-card__eyebrow {
  transition: color 0.3s ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
}

// A done task stays in the list, dimmed and marked, so the list does not reshuffle underfoot.
// The dimming is deliberately NOT an opacity on the copy: text composited through opacity falls
// below the 4.5:1 it needs (measured 2.66:1 on the eyebrow at 0.55) and axe cannot evaluate it.
// The decoration dims; the words switch to the muted-but-legible neutral; and the tick — the
// marker that has to stay legible once the text recedes — is not dimmed at all.
.item-card--done .item-card__thumb {
  opacity: 0.55;
}

.item-card--done .item-card__title,
.item-card--done .item-card__eyebrow {
  color: var(--Neutral-UI-neutral-ui-70);
}

.item-card__thumb {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: v-bind(thumbSize);
  height: v-bind(thumbSize);
  border-radius: 999px;
  overflow: hidden;
  color: var(--today-card-ink);
}

.item-card__thumb--session { background: var(--today-card-tint-session); }

.item-card__thumb--homework {
  background: var(--today-card-tint-homework);
  color: var(--today-card-ink-on-amber);
}

.item-card__thumb--challenge { background: var(--today-card-tint-challenge); }
.item-card__thumb--assessment { background: var(--today-card-tint-assessment); }
.item-card__thumb--program { background: var(--today-card-tint-program); }

.item-card__thumb--content {
  background: var(--today-card-tint-content);
  // Same pinned ink as homework: this tint is the same amber in dark mode, where the theme-aware
  // ink would compute to 1.66:1 on it. See the token file for the whole story.
  color: var(--today-card-ink-on-amber);
}

// skip-loader renders a bare <img>: a q-img's wrapper divs and skeleton are not valid
// content inside a <button>, and the skeleton flashes on a 42px thumbnail anyway.
.item-card__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

// The eyebrow, the title and the bar sit almost on top of each other in the design: 3px apart.
// A literal, because the repo's spacing scale has no step between 0 and 8px on desktop, and
// borrowing the 8px one is what made this card 19px taller than the design.
.item-card__body {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  flex: 1;
}

// The caption step resolves to 10px on desktop; the design sets the card's eyebrow at 8px, which
// is what keeps the title reading as the dominant line of the pair. Only the size is overridden —
// the uppercasing, the tracking and the weight are the caption's.
.item-card__eyebrow {
  @include font-caption-sm;

  font-size: var(--today-text-card-eyebrow);
  color: var(--Neutral-UI-neutral-ui-80);
}

// Titles come from the CMS and can be long; one line with an ellipsis keeps every row the
// same height, which is what lets the list read as a list.
.item-card__title {
  @include font-body-bold;

  font-size: var(--today-text-card-title);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  .item-card--row & {
    @include font-body-sm-bold;

    font-size: var(--today-text-row-title);
  }
}

// The bar sits a little further from the title than the title from the eyebrow: 6px against 3px,
// so the pair of text lines stays together and the bar reads as a separate thing.
.item-card__track {
  width: 160px;
  max-width: 100%;
  height: 5px;
  margin-top: 3px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--Neutral-UI-neutral-ui-100) 16%, transparent);
  overflow: hidden;
}

.item-card__fill {
  display: block;
  height: 100%;
  border-radius: 999px;
  // The alias exists for exactly this fill: the theme-aware primary goes pale mint in dark mode
  // and stops reading as an accent (see the token file). Keeps both progress bars on the page —
  // this one and the program rows' — the same green.
  background: var(--today-accent-complete);
}

.item-card__arrow {
  flex: none;
  color: var(--Neutral-UI-neutral-ui-100);
}

// The chevron is the row's affordance, quieter than the featured card's arrow.
.item-card__chevron {
  flex: none;
  color: var(--Neutral-UI-neutral-ui-80);
}

// Green, as in the design: the tick is what says "done" once the copy has dimmed, so it keeps
// full opacity. The palette's green cannot be used neat — it is the same #259275 in both schemes
// and lands at 2.7:1 on the tile surface, under the 3:1 a meaningful graphic needs, and here the
// tick is the only remaining signal that the row is finished. Mixing a quarter of the theme ink
// into it darkens the green on light and lightens it on dark, which takes both schemes past the
// floor (3.7:1 and 3.9:1) while leaving it unmistakably green.
.item-card__check {
  flex: none;
  color: color-mix(in srgb, var(--Functional-wl-completed-surface) 75%, var(--Neutral-UI-neutral-ui-100));
}

// Swapping the chevron for the tick: only a completion that happens on screen animates.
.item-card-mark-enter-active,
.item-card-mark-leave-active {
  transition: transform 0.15s ease-out, opacity 0.15s ease-out;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
}

.item-card-mark-enter-from,
.item-card-mark-leave-to {
  transform: scale(0.4);
  opacity: 0;
}
</style>
